import React, { useState, useEffect, useRef } from "react";
import { createRoot, Root } from "react-dom/client";
import IMask from "imask";

interface FormField {
  name: string;
  type: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  options?: Array<{ label: string; value: string }>;
}

interface FormTemplate {
  id: number;
  form_name: string;
  form_title: string;
  form_fields: FormField[];
  form_action: string;
  success_message?: string;
}

interface DynamicFormProps {
  formName: string;
  triggerButton?: HTMLElement;
  buttonText?: string;
  buttonClass?: string;
  inline?: boolean; // Если true, форма отображается inline без popup
  hideTrigger?: boolean; // Программное открытие без кнопки
  initialOpen?: boolean;
  initialPrefill?: PrefillValues;
}

export type PrefillValues = Record<string, string | boolean | number | undefined | null>;

type SubmitStatus = "success" | "error" | null;

type FormOpener = (values?: PrefillValues) => void;

const formOpeners = new Map<string, FormOpener>();
const pendingPrefills = new Map<string, PrefillValues>();
const programmaticHosts = new Map<string, HTMLElement>();

function applyPrefill(form: HTMLFormElement, values: PrefillValues): void {
  Object.entries(values).forEach(([name, raw]) => {
    if (raw === undefined || raw === null) return;

    let el = form.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${CSS.escape(name)}"]`);

    // Если поля нет в шаблоне — добавляем hidden, чтобы значение ушло в заявку/письмо
    if (!el && (typeof raw === "string" || typeof raw === "number")) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = name;
      form.appendChild(hidden);
      el = hidden;
    }
    if (!el) return;

    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      el.checked = raw === true || raw === 1 || raw === "1" || raw === "true";
      el.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    const str = String(raw);
    const phoneInput = el as HTMLInputElement;
    if (phoneInput.type === "tel" && (phoneInput as any).imask) {
      (phoneInput as any).imask.value = str;
    } else {
      el.value = str;
    }
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

// Получение API URL (из переменной окружения или относительный путь)
function getApiUrl(): string {
  // В dev режиме может быть задан через window.API_URL или переменную окружения
  if (typeof window !== "undefined" && (window as any).API_URL) {
    return (window as any).API_URL;
  }
  // По умолчанию относительный путь
  return "/api.php";
}

/**
 * Компонент динамической формы, загружаемой из API
 */
function DynamicForm({
  formName,
  triggerButton,
  buttonText,
  buttonClass,
  inline = false,
  hideTrigger = false,
  initialOpen = false,
  initialPrefill = {},
}: DynamicFormProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState<boolean>(inline || initialOpen);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const [formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);
  const [error, setError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [prefill, setPrefill] = useState<PrefillValues>(initialPrefill);
  const formRef = useRef<HTMLFormElement | null>(null);
  const prefillRef = useRef<PrefillValues>(initialPrefill);
  prefillRef.current = prefill;

  const openPopup = (values?: PrefillValues): void => {
    if (values) {
      setPrefill(values);
      prefillRef.current = values;
    }
    setSubmitStatus(null);
    setSubmitError("");
    setIsOpen(true);
    if (!inline) {
      document.body.style.overflow = "hidden";
    }
  };

  // Регистрация программного открытия (openDynamicForm)
  useEffect(() => {
    const opener: FormOpener = (values) => openPopup(values);
    formOpeners.set(formName, opener);
    const pending = pendingPrefills.get(formName);
    if (pending) {
      pendingPrefills.delete(formName);
      openPopup(pending);
    }
    return () => {
      if (formOpeners.get(formName) === opener) {
        formOpeners.delete(formName);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formName]);

  // Загружаем форму при открытии / inline
  useEffect(() => {
    if ((inline || isOpen) && !formTemplate && !isLoading) {
      loadForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inline, isOpen]);

  const closePopup = (): void => {
    if (!inline) {
      setIsOpen(false);
      document.body.style.overflow = "";
    }
    setSubmitStatus(null);
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  const loadForm = async (): Promise<void> => {
    setIsLoading(true);
    setError("");

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}?action=get_template&name=${encodeURIComponent(formName)}`);
      const result = await response.json();

      if (result.success) {
        setFormTemplate(result.data);
      } else {
        setError(result.message || "Ошибка загрузки формы");
      }
    } catch (err) {
      setError("Ошибка подключения к серверу");
      console.error("Form loading error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formTemplate || !formRef.current) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitError("");

    const formData = new FormData(formRef.current);
    // Добавляем имя формы для идентификации
    formData.append("form_name", formTemplate.form_name);

    // Гарантируем префилл (topic и др.) даже если DOM-поле сбросилось
    Object.entries(prefillRef.current).forEach(([key, raw]) => {
      if (raw === undefined || raw === null) return;
      if (typeof raw === "boolean") {
        if (raw) {
          formData.set(key, "1");
        }
        return;
      }
      const str = String(raw);
      if (str !== "") {
        formData.set(key, str);
      }
    });
    const action = formTemplate.form_action || "/form.php";

    try {
      const response = await fetch(action, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        formRef.current.reset();

        // Обновляем CAPTCHA, если есть
        const captchaImage = formRef.current.querySelector<HTMLImageElement>("img[data-captcha-image]");
        if (captchaImage) {
          captchaImage.src = `/captcha.php?${Math.random()}`;
        }

        // Закрываем popup через 3 секунды
        setTimeout(() => {
          closePopup();
        }, 3000);
      } else {
        setSubmitStatus("error");
        setSubmitError(result.message || "Произошла ошибка. Пожалуйста, попробуйте позже.");

        // Обновляем CAPTCHA при ошибке
        const captchaImage = formRef.current.querySelector<HTMLImageElement>('img[src*="captcha.php"]');
        if (captchaImage) {
          captchaImage.src = `/captcha.php?${Math.random()}`;
        }

        const captchaInput = formRef.current.querySelector<HTMLInputElement>('input[name="captcha"]');
        if (captchaInput) {
          captchaInput.value = "";
        }
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitStatus("error");
      setSubmitError("Ошибка подключения к серверу. Пожалуйста, проверьте подключение к интернету и попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Инициализация масок и CAPTCHA после загрузки формы
  useEffect(() => {
    if (!formTemplate || !formRef.current || !isOpen) return;

    // Инициализация маски телефона для всех полей телефона
    const phoneInputs = formRef.current.querySelectorAll<HTMLInputElement>('input[type="tel"], input[name="phone"], input[data-phone-input]');

    const cleanupFunctions: Array<() => void> = [];

    phoneInputs.forEach((phoneInput) => {
      // Удаляем старую маску, если она существует
      if ((phoneInput as any).imask) {
        (phoneInput as any).imask.destroy();
        delete (phoneInput as any).imask;
      }

      // Создаем новую маску
      const mask = IMask(phoneInput, {
        mask: "+7 (000) 000-00-00",
        lazy: false,
        placeholderChar: "_",
      });

      // Сохраняем ссылку на маску
      (phoneInput as any).imask = mask;

      // Обработка фокуса
      const handleFocus = () => {
        if (!phoneInput.value || phoneInput.value.trim() === "" || phoneInput.value === "+7 (___) ___-__-__") {
          mask.value = "+7";
          phoneInput.setSelectionRange(2, 2);
        }
      };

      // Обработка blur
      const handleBlur = () => {
        const cleanValue = phoneInput.value.replace(/\D/g, "");
        if (cleanValue === "7" || cleanValue === "" || phoneInput.value === "+7" || phoneInput.value === "+7 (___) ___-__-__") {
          mask.value = "";
        }
      };

      phoneInput.addEventListener("focus", handleFocus);
      phoneInput.addEventListener("blur", handleBlur);

      // Сохраняем cleanup функцию
      cleanupFunctions.push(() => {
        phoneInput.removeEventListener("focus", handleFocus);
        phoneInput.removeEventListener("blur", handleBlur);
        if ((phoneInput as any).imask) {
          (phoneInput as any).imask.destroy();
          delete (phoneInput as any).imask;
        }
      });
    });

    // Cleanup функция для всех масок
    applyPrefill(formRef.current, prefillRef.current);

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [formTemplate, isOpen, prefill]);

  // Управление показом CAPTCHA
  useEffect(() => {
    if (!formTemplate || !formRef.current || !isOpen) return;

    applyPrefill(formRef.current, prefillRef.current);

    const captchaContainer = formRef.current.querySelector<HTMLElement>(".captcha-container");
    if (captchaContainer) {
      const requiredFields = formRef.current.querySelectorAll<HTMLInputElement>('input[required]:not([name="captcha"]):not([name="consent"]):not([type="hidden"])');
      const consentCheckbox = formRef.current.querySelector<HTMLInputElement>('input[name="consent"]');

      const checkFields = (): void => {
        let allFilled = true;
        requiredFields.forEach((field) => {
          const value = field.value.trim();
          if (!value) {
            allFilled = false;
          } else if (field.type === "tel") {
            const digits = value.replace(/\D/g, "");
            if (digits.length < 11) {
              allFilled = false;
            }
          }
        });
        if (consentCheckbox && !consentCheckbox.checked) {
          allFilled = false;
        }
        if (allFilled) {
          captchaContainer.classList.remove("hidden");
          const captchaInput = formRef.current?.querySelector<HTMLInputElement>('input[name="captcha"]');
          if (captchaInput) {
            captchaInput.required = true;
          }
          // Загружаем CAPTCHA только когда контейнер становится видимым
          const captchaImage = formRef.current?.querySelector<HTMLImageElement>("img[data-captcha-image]");
          if (captchaImage && !captchaImage.dataset.loaded) {
            captchaImage.src = `/captcha.php?${Math.random()}`;
            captchaImage.dataset.loaded = "true";
          }
        } else {
          captchaContainer.classList.add("hidden");
          const captchaInput = formRef.current?.querySelector<HTMLInputElement>('input[name="captcha"]');
          if (captchaInput) {
            captchaInput.required = false;
            captchaInput.value = "";
          }
          // Сбрасываем флаг загрузки при скрытии контейнера
          const captchaImage = formRef.current?.querySelector<HTMLImageElement>("img[data-captcha-image]");
          if (captchaImage) {
            captchaImage.removeAttribute("src");
            captchaImage.removeAttribute("data-loaded");
          }
        }
      };

      requiredFields.forEach((field) => {
        field.addEventListener("input", checkFields);
        field.addEventListener("change", checkFields);
      });
      if (consentCheckbox) {
        consentCheckbox.addEventListener("change", checkFields);
      }
      checkFields();

      return () => {
        requiredFields.forEach((field) => {
          field.removeEventListener("input", checkFields);
          field.removeEventListener("change", checkFields);
        });
        if (consentCheckbox) {
          consentCheckbox.removeEventListener("change", checkFields);
        }
      };
    }

    // Обработчик обновления CAPTCHA
    const captchaImage = formRef.current.querySelector<HTMLImageElement>("img[data-captcha-image]");
    if (captchaImage) {
      const onClick = () => {
        captchaImage.src = `/captcha.php?${Math.random()}`;
      };
      captchaImage.addEventListener("click", onClick);
      return () => captchaImage.removeEventListener("click", onClick);
    }
  }, [formTemplate, isOpen, prefill]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && isOpen) {
        closePopup();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  const renderField = (field: FormField): JSX.Element => {
    const fieldId = `field-${field.name}`;

    switch (field.type) {
      case "textarea":
        return (
          <div key={field.name}>
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
              dangerouslySetInnerHTML={{
                __html: field.label + (field.required ? ' <span class="text-red-500">*</span>' : ""),
              }}
            />
            <textarea id={fieldId} name={field.name} required={field.required} placeholder={field.placeholder} rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
        );

      case "select":
        return (
          <div key={field.name}>
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
              dangerouslySetInnerHTML={{
                __html: field.label + (field.required ? ' <span class="text-red-500">*</span>' : ""),
              }}
            />
            <select id={fieldId} name={field.name} required={field.required} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
              {field.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );

      case "checkbox":
        return (
          <div key={field.name}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" id={fieldId} name={field.name} value={field.value || "1"} required={field.required} className=" w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
              <span
                className="text-xs text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: field.label + (field.required ? ' <span class="text-red-500">*</span>' : ""),
                }}
              />
            </label>
          </div>
        );

      case "hidden":
        return (
          <input
            key={field.name}
            type="hidden"
            id={fieldId}
            name={field.name}
            value={
              prefill[field.name] !== undefined && prefill[field.name] !== null
                ? String(prefill[field.name])
                : field.value || ""
            }
            readOnly
          />
        );

      case "tel":
        return (
          <div key={field.name}>
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
              dangerouslySetInnerHTML={{
                __html: field.label + (field.required ? ' <span class="text-red-500">*</span>' : ""),
              }}
            />
            <input type="tel" id={fieldId} name={field.name} required={field.required} placeholder={field.placeholder || "+7 (999) 123-45-67"} data-phone-input className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
        );

      case "date":
        return (
          <div key={field.name}>
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
              dangerouslySetInnerHTML={{
                __html: field.label + (field.required ? ' <span class="text-red-500">*</span>' : ""),
              }}
            />
            <input type="date" id={fieldId} name={field.name} required={field.required} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
        );

      default:
        return (
          <div key={field.name}>
            <label
              htmlFor={fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
              dangerouslySetInnerHTML={{
                __html: field.label + (field.required ? ' <span class="text-red-500">*</span>' : ""),
              }}
            />
            <input type={field.type || "text"} id={fieldId} name={field.name} required={field.required} placeholder={field.placeholder} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
        );
    }
  };

  // Рендерим кнопку, если popup закрыт и не inline режим
  if (!isOpen && !inline) {
    if (hideTrigger) {
      return null;
    }
    const displayText = buttonText || triggerButton?.textContent || "Открыть форму";
    const displayClass = buttonClass || triggerButton?.className || "btn-primary";

    return (
      <button onClick={() => openPopup()} className={displayClass} data-trigger-dynamic-form={formName}>
        {displayText}
      </button>
    );
  }

  // Inline режим - отображаем форму напрямую
  if (inline) {
    return (
      <div>
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {formTemplate && !isLoading && submitStatus !== "success" && (
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            {formTemplate.form_fields.map((field) => renderField(field))}

            {/* CAPTCHA контейнер - будет показан после заполнения обязательных полей */}
            <div className="captcha-container hidden">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Код с картинки <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3">
                <input type="text" name="captcha" required placeholder="Введите код" maxLength={10} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase" style={{ textTransform: "uppercase" }} />
                <div className="flex-shrink-0">
                  <img data-captcha-image alt="CAPTCHA" className="h-12 w-auto border border-gray-300 rounded cursor-pointer" title="Нажмите для обновления" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Нажмите на картинку, чтобы обновить код</p>
            </div>

            {submitError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-800 text-sm font-medium">{submitError}</p>
                </div>
              </div>
            )}

            <button type="submit" disabled={isSubmitting} className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting ? "Отправка..." : "Отправить"}
            </button>
          </form>
        )}

        {submitStatus === "success" && (
          <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800 text-base font-medium">{formTemplate?.success_message || "Спасибо! Ваша заявка принята. Мы свяжемся с вами в ближайшее время."}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Popup режим
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">{formTemplate?.form_title || "Загрузка формы..."}</h2>
          <button onClick={closePopup} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {isLoading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="text-gray-600 text-sm mt-2">Загрузка формы...</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {formTemplate && !isLoading && submitStatus !== "success" && (
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              {formTemplate.form_fields.map((field) => renderField(field))}

              {/* CAPTCHA контейнер - будет показан после заполнения обязательных полей */}
              <div className="captcha-container hidden">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Код с картинки <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input type="text" name="captcha" required placeholder="Введите код" maxLength={10} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase" style={{ textTransform: "uppercase" }} />
                  <div className="flex-shrink-0">
                    <img data-captcha-image alt="CAPTCHA" className="h-12 w-auto border border-gray-300 rounded cursor-pointer" title="Нажмите для обновления" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Нажмите на картинку, чтобы обновить код</p>
              </div>

              {submitError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 text-sm font-medium">{submitError}</p>
                  </div>
                </div>
              )}

              <button type="submit" disabled={isSubmitting} className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? "Отправка..." : "Отправить"}
              </button>
            </form>
          )}

          {submitStatus === "success" && (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-800 text-base font-medium">{formTemplate?.success_message || "Спасибо! Ваша заявка принята. Мы свяжемся с вами в ближайшее время."}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Хранилище для уже созданных roots
const rootsMap = new WeakMap<HTMLElement, Root>();

/**
 * Инициализация динамических форм
 * Ищет элементы с data-trigger-dynamic-form атрибутом
 */
export function initDynamicForms(): void {
  const containers = document.querySelectorAll<HTMLElement>(".dynamic-form-container");
  const triggerButtons = document.querySelectorAll<HTMLElement>("[data-trigger-dynamic-form]");

  // Обрабатываем контейнеры
  containers.forEach((container) => {
    if (container.dataset.reactInitialized === "true") {
      return;
    }

    const formName = container.dataset.formName;
    if (!formName) return;

    // Проверяем, нужен ли inline режим
    const isInline = container.dataset.inline === "true" || container.hasAttribute("data-inline");

    // Получаем данные кнопки из атрибутов контейнера
    const buttonText = container.dataset.buttonText || "Открыть форму";
    const buttonClass = container.dataset.buttonClass || "btn-primary";
    const buttonId = container.dataset.buttonId;

    // Ищем существующую кнопку (только если не inline)
    let existingButton: HTMLElement | null = null;
    if (!isInline) {
      existingButton = buttonId ? document.getElementById(buttonId) : container.querySelector<HTMLElement>("button");

      // Пропускаем кнопки, которые уже обрабатываются PopupForm
      if (existingButton && existingButton.dataset.popupFormInitialized === "true") {
        return;
      }
    }

    // Создаем виртуальную кнопку для передачи данных (если кнопка не найдена и не inline)
    const virtualButton = existingButton || (isInline ? undefined : document.createElement("button"));
    if (!existingButton && !isInline && virtualButton) {
      virtualButton.className = buttonClass;
      virtualButton.textContent = buttonText;
    }

    let root = rootsMap.get(container);
    if (!root) {
      root = createRoot(container);
      rootsMap.set(container, root);
    }

    root.render(<DynamicForm formName={formName} triggerButton={virtualButton} buttonText={buttonText} buttonClass={buttonClass} inline={isInline} />);
    container.dataset.reactInitialized = "true";
  });

  // Обрабатываем отдельные кнопки (только те, которые НЕ находятся внутри контейнера)
  triggerButtons.forEach((button) => {
    if (button.dataset.reactInitialized === "true" || button.dataset.popupFormInitialized === "true") {
      return;
    }

    // Пропускаем кнопки, которые уже находятся внутри dynamic-form-container
    if (button.closest(".dynamic-form-container")) {
      return;
    }

    const formName = button.dataset.triggerDynamicForm;
    if (!formName) return;

    // Создаем контейнер для формы
    const container = document.createElement("div");
    container.className = "dynamic-form-container";
    container.dataset.formName = formName;
    container.style.display = "none";
    document.body.appendChild(container);

    let root = rootsMap.get(container);
    if (!root) {
      root = createRoot(container);
      rootsMap.set(container, root);
    }

    root.render(<DynamicForm formName={formName} triggerButton={button} />);
    button.dataset.reactInitialized = "true";
  });
}

/**
 * Программное открытие динамической формы с предзаполнением полей.
 */
export function openDynamicForm(formName: string, values: PrefillValues = {}): void {
  const opener = formOpeners.get(formName);
  if (opener) {
    opener(values);
    return;
  }

  pendingPrefills.set(formName, values);

  let host = programmaticHosts.get(formName);
  if (!host) {
    host = document.createElement("div");
    host.className = "dynamic-form-programmatic-host";
    host.dataset.formName = formName;
    document.body.appendChild(host);
    programmaticHosts.set(formName, host);

    const root = createRoot(host);
    rootsMap.set(host, root);
    root.render(<DynamicForm formName={formName} hideTrigger initialOpen initialPrefill={values} />);
  }
}

/**
 * Блок «Узнать стоимость»: телефон + consent на странице → модалка find-out-the-cost.
 * Валидация — нативная браузерная (required / reportValidity / setCustomValidity).
 */
export function initCostLeadForms(): void {
  const forms = document.querySelectorAll<HTMLFormElement>("form[data-cost-lead-form]");

  forms.forEach((form) => {
    if (form.dataset.costLeadInitialized === "true") return;
    form.dataset.costLeadInitialized = "true";

    const formName = form.dataset.formName || "find-out-the-cost";
    const phoneInput = form.querySelector<HTMLInputElement>('input[name="phone"]');
    const consentInput = form.querySelector<HTMLInputElement>('input[name="consent"]');

    const syncPhoneValidity = (): void => {
      if (!phoneInput) return;
      const digits = phoneInput.value.replace(/\D/g, "");
      if (digits.length < 11) {
        phoneInput.setCustomValidity("Укажите номер телефона полностью");
      } else {
        phoneInput.setCustomValidity("");
      }
    };

    phoneInput?.addEventListener("input", syncPhoneValidity);
    phoneInput?.addEventListener("blur", syncPhoneValidity);
    syncPhoneValidity();

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      syncPhoneValidity();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const topic = form.getAttribute("data-topic") || form.dataset.topic || "";

      openDynamicForm(formName, {
        phone: phoneInput?.value?.trim() || "",
        consent: consentInput ? consentInput.checked : true,
        topic,
      });
    });
  });
}
